
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import {
  ArrowUpRight,
  ArrowDownRight,
  Users,
  Package,
  ShoppingCart,
  DollarSign,
  LayoutGrid,
  TrendingUp,
  PackagePlus,
  Boxes,
  Megaphone,
  Bell,
  Activity,
  ChartPie,
  Clock,
  Star,
  Plus,
  Eye,
  FileText,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Sample data for charts
const salesData = [
  { name: "Mon", jimatshop: 400, jimatbiz: 240, walkin: 240 },
  { name: "Tue", jimatshop: 300, jimatbiz: 139, walkin: 221 },
  { name: "Wed", jimatshop: 200, jimatbiz: 980, walkin: 190 },
  { name: "Thu", jimatshop: 278, jimatbiz: 390, walkin: 200 },
  { name: "Fri", jimatshop: 189, jimatbiz: 480, walkin: 218 },
  { name: "Sat", jimatshop: 239, jimatbiz: 380, walkin: 250 },
  { name: "Sun", jimatshop: 349, jimatbiz: 430, walkin: 210 },
];

const pieData = [
  { name: "Walk-in", value: 55 },
  { name: "Online", value: 45 },
];

const COLORS = ["#4D9E41", "#8CC84B", "#A4E173"];

const topProducts = [
  { id: 1, name: "Organic Apples", quantity: 230, revenue: 1150, margin: 22 },
  { id: 2, name: "Fresh Bread", quantity: 189, revenue: 945, margin: 18 },
  { id: 3, name: "Milk 1L", quantity: 168, revenue: 504, margin: 15 },
  { id: 4, name: "Eggs (12pk)", quantity: 145, revenue: 580, margin: 25 },
  { id: 5, name: "Chicken Breast", quantity: 132, revenue: 924, margin: 20 },
];

const forecastData = [
  { day: "Mon", forecast: 320 },
  { day: "Tue", forecast: 332 },
  { day: "Wed", forecast: 301 },
  { day: "Thu", forecast: 334 },
  { day: "Fri", forecast: 390 },
  { day: "Sat", forecast: 330 },
  { day: "Sun", forecast: 320 },
];

const Dashboard = () => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#333333]">Dashboard</h1>
        <p className="text-muted-foreground">Welcome to the JiMATPOS dashboard</p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-[#F2FCE2] border border-[#8CC84B]/20">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#8CC84B] data-[state=active]:text-white">
            <LayoutGrid className="w-4 h-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="sales" className="data-[state=active]:bg-[#8CC84B] data-[state=active]:text-white">
            <TrendingUp className="w-4 h-4 mr-2" /> Sales Summary
          </TabsTrigger>
          <TabsTrigger value="orders" className="data-[state=active]:bg-[#8CC84B] data-[state=active]:text-white">
            <PackagePlus className="w-4 h-4 mr-2" /> Recommended Orders
          </TabsTrigger>
          <TabsTrigger value="stock" className="data-[state=active]:bg-[#8CC84B] data-[state=active]:text-white">
            <Boxes className="w-4 h-4 mr-2" /> Stock Snapshot
          </TabsTrigger>
          <TabsTrigger value="promotion" className="data-[state=active]:bg-[#8CC84B] data-[state=active]:text-white">
            <Megaphone className="w-4 h-4 mr-2" /> Promotion Performance
          </TabsTrigger>
          <TabsTrigger value="alerts" className="data-[state=active]:bg-[#8CC84B] data-[state=active]:text-white">
            <Bell className="w-4 h-4 mr-2" /> Alerts & Notifications
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card className="border-[#8CC84B]/20 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-[#4D9E41]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">RM 45,231.89</div>
                <p className="text-xs text-muted-foreground flex items-center">
                  <span className="flex items-center text-[#4D9E41]">
                    <ArrowUpRight className="mr-1 h-4 w-4" />
                    +20.1%
                  </span> 
                  from last month
                </p>
              </CardContent>
            </Card>
            <Card className="border-[#8CC84B]/20 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Sales</CardTitle>
                <ShoppingCart className="h-4 w-4 text-[#4D9E41]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">+2350</div>
                <p className="text-xs text-muted-foreground flex items-center">
                  <span className="flex items-center text-[#4D9E41]">
                    <ArrowUpRight className="mr-1 h-4 w-4" />
                    +8.2%
                  </span> 
                  from last month
                </p>
              </CardContent>
            </Card>
            <Card className="border-[#8CC84B]/20 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Products</CardTitle>
                <Package className="h-4 w-4 text-[#4D9E41]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">1,234</div>
                <p className="text-xs text-muted-foreground flex items-center">
                  <span className="flex items-center text-[#4D9E41]">
                    <ArrowUpRight className="mr-1 h-4 w-4" />
                    +12.5%
                  </span> 
                  from last month
                </p>
              </CardContent>
            </Card>
            <Card className="border-[#8CC84B]/20 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Customers</CardTitle>
                <Users className="h-4 w-4 text-[#4D9E41]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">5,437</div>
                <p className="text-xs text-muted-foreground flex items-center">
                  <span className="flex items-center text-red-600">
                    <ArrowDownRight className="mr-1 h-4 w-4" />
                    -2.3%
                  </span> 
                  from last month
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Real-Time Sales Monitor */}
          <Card className="col-span-1 lg:col-span-2 shadow-sm border-[#8CC84B]/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-medium flex items-center">
                <Activity className="w-5 h-5 mr-2 text-[#4D9E41]" />
                Real-Time Sales Monitor
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={salesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5F5DA" />
                    <XAxis dataKey="name" stroke="#8CC84B" />
                    <YAxis stroke="#8CC84B" />
                    <Tooltip />
                    <Area type="monotone" dataKey="jimatshop" stackId="1" stroke="#4D9E41" fill="#4D9E41" fillOpacity={0.6} />
                    <Area type="monotone" dataKey="jimatbiz" stackId="1" stroke="#8CC84B" fill="#8CC84B" fillOpacity={0.6} />
                    <Area type="monotone" dataKey="walkin" stackId="1" stroke="#A4E173" fill="#A4E173" fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 mt-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-[#4D9E41] rounded-full mr-2" />
                  <span className="text-xs">JimatShop</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-[#8CC84B] rounded-full mr-2" />
                  <span className="text-xs">JimatBiz</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-[#A4E173] rounded-full mr-2" />
                  <span className="text-xs">Walk-In</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Top Selling Products */}
            <Card className="col-span-1 shadow-sm border-[#8CC84B]/20">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Package className="w-5 h-5 mr-2 text-[#4D9E41]" />
                  Top Products
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-[#4D9E41]">
                        <th className="py-2 px-4 font-medium">Product</th>
                        <th className="py-2 px-4 font-medium">Qty</th>
                        <th className="py-2 px-4 font-medium">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topProducts.slice(0, 3).map((product) => (
                        <tr key={product.id} className="border-b hover:bg-[#F2FCE2]">
                          <td className="py-2 px-4">{product.name}</td>
                          <td className="py-2 px-4">{product.quantity}</td>
                          <td className="py-2 px-4">RM {product.revenue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            
            {/* Quick Stats Panel */}
            <Card className="col-span-1 shadow-sm border-[#8CC84B]/20">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ChartPie className="w-5 h-5 mr-2 text-[#4D9E41]" />
                  Sales Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[150px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={60}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Shortcuts / Quick Links */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Button variant="outline" className="h-24 flex flex-col items-center justify-center border-[#8CC84B]/20 hover:bg-[#F2FCE2] hover:text-[#4D9E41] hover:border-[#8CC84B]">
              <Plus className="h-6 w-6 mb-1" />
              <span>Add Product</span>
            </Button>
            <Button variant="outline" className="h-24 flex flex-col items-center justify-center border-[#8CC84B]/20 hover:bg-[#F2FCE2] hover:text-[#4D9E41] hover:border-[#8CC84B]">
              <ShoppingCart className="h-6 w-6 mb-1" />
              <span>JimatShop Sales</span>
            </Button>
            <Button variant="outline" className="h-24 flex flex-col items-center justify-center border-[#8CC84B]/20 hover:bg-[#F2FCE2] hover:text-[#4D9E41] hover:border-[#8CC84B]">
              <PackagePlus className="h-6 w-6 mb-1" />
              <span>JimatBiz Order</span>
            </Button>
            <Button variant="outline" className="h-24 flex flex-col items-center justify-center border-[#8CC84B]/20 hover:bg-[#F2FCE2] hover:text-[#4D9E41] hover:border-[#8CC84B]">
              <Megaphone className="h-6 w-6 mb-1" />
              <span>Start Promotion</span>
            </Button>
            <Button variant="outline" className="h-24 flex flex-col items-center justify-center border-[#8CC84B]/20 hover:bg-[#F2FCE2] hover:text-[#4D9E41] hover:border-[#8CC84B]">
              <Eye className="h-6 w-6 mb-1" />
              <span>View Orders</span>
            </Button>
            <Button variant="outline" className="h-24 flex flex-col items-center justify-center border-[#8CC84B]/20 hover:bg-[#F2FCE2] hover:text-[#4D9E41] hover:border-[#8CC84B]">
              <FileText className="h-6 w-6 mb-1" />
              <span>Generate Report</span>
            </Button>
          </div>
        </TabsContent>
        
        {/* Placeholder content for other tabs */}
        <TabsContent value="sales" className="mt-6">
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardHeader>
              <CardTitle>Sales Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Sales summary content will be implemented here.</p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="orders" className="mt-6">
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardHeader>
              <CardTitle>Recommended Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Recommended orders content will be implemented here.</p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="stock" className="mt-6">
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardHeader>
              <CardTitle>Stock Snapshot</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Stock snapshot content will be implemented here.</p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="promotion" className="mt-6">
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardHeader>
              <CardTitle>Promotion Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Promotion performance content will be implemented here.</p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="alerts" className="mt-6">
          <Card className="shadow-sm border-[#8CC84B]/20">
            <CardHeader>
              <CardTitle>Alerts & Notifications</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Alerts and notifications content will be implemented here.</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
